import React, { useMemo } from 'react';
import DecisionEngineBlock from '../../decision-engine/DecisionEngineBlock';
import {
  aksiyonKilidiHesapla,
  aksiyonSkoruHesapla,
  aksiyonlaraKilitUygula,
  guvenBandiHesapla
} from '../../decision-engine/decisionEngineScoring';
import DrawerFooterActions from './DrawerFooterActions';
import { olusturAksiyonDurumKapsami } from '../../decision-engine/actionStateScope';

function Metric({ label, value }) {
  return (
    <li className="flex items-center justify-between text-sm">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="font-black text-slate-900 dark:text-white">{value}</span>
    </li>
  );
}

function RecipeList({ title, rows, valueKey = 'cook' }) {
  const items = Array.isArray(rows) ? rows.slice(0, 10) : [];
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">{title}</p>
      {items.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Bu filtrede gösterilecek kayıt yok.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {items.map((item) => (
            <li key={`${title}-${item.recipeId}`} className="flex items-center justify-between gap-2 text-sm">
              <span className="truncate text-slate-700 dark:text-slate-200">{item.recipeName || `Tarif #${item.recipeId}`}</span>
              <span className="font-black text-slate-900 dark:text-white">{Number(item?.counts?.[valueKey] || 0).toLocaleString('tr-TR')}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const KITCHEN_TYPE_LABELS = {
  firin: 'Fırın',
  tava: 'Tava',
  tencere: 'Tencere',
  hizli: 'Hızlı',
  diger: 'Diğer'
};

const SEGMENT_LABELS = {
  bebekli: 'Bebekli',
  alerjanli: 'Alerjanlı',
  premium: 'Premium',
  uzmanli: 'Uzmanlı',
  'yogun-calisan': 'Yoğun Çalışan',
  'ev-hanimi': 'Ev Hanımı',
  ogrenci: 'Öğrenci',
  emekli: 'Emekli'
};

function normalizeKitchenType(rawCategory = '', rawName = '') {
  const text = `${String(rawCategory || '')} ${String(rawName || '')}`.toLocaleLowerCase('tr-TR');
  if (text.includes('fırın') || text.includes('firin')) return 'firin';
  if (text.includes('tava')) return 'tava';
  if (text.includes('tencere') || text.includes('çorba') || text.includes('corba')) return 'tencere';
  if (text.includes('hızlı') || text.includes('hizli') || text.includes('sandviç') || text.includes('sandvic') || text.includes('salata')) return 'hizli';
  return 'diger';
}

function categoryHint(typeKey) {
  if (typeKey === 'firin') return 'Fırın yemekleri görünürlüğü';
  if (typeKey === 'tava') return 'Tava yemekleri akış performansı';
  if (typeKey === 'tencere') return 'Tencere yemeklerinde pişirme sinyali';
  if (typeKey === 'hizli') return 'Hızlı tarif tüketim eğilimi';
  return 'Diğer kategoriler';
}

function withRates(rows = []) {
  return rows.map((row) => {
    const view = Number(row?.view || 0);
    const select = Number(row?.select || 0);
    const plan = Number(row?.plan || 0);
    const cook = Number(row?.cook || 0);
    const viewToSelect = view > 0 ? Number(((select / view) * 100).toFixed(2)) : 0;
    const selectToPlan = select > 0 ? Number(((plan / select) * 100).toFixed(2)) : 0;
    const planToCook = plan > 0 ? Number(((cook / plan) * 100).toFixed(2)) : 0;
    return { ...row, view, select, plan, cook, viewToSelect, selectToPlan, planToCook };
  });
}

export default function FunnelDetailDrawer({ entity, onClose }) {
  const payload = entity?.payload || {};
  const filters = entity?.filters || {};
  const funnel = payload?.funnel || (payload?.view != null ? payload : null);
  const decisionFeedback = payload?.decisionFeedback || null;
  const recipePerformance = payload?.recipePerformance || null;
  const unusedRecipes = payload?.unusedRecipes || null;
  const kitchenTypeIntelligence = payload?.kitchenTypeIntelligence || null;

  const fallbackTypeRows = useMemo(() => {
    const recipeMap = new Map();
    const pushRows = (rows = []) => {
      for (const row of rows) {
        const recipeId = String(row?.recipeId || '').trim();
        if (!recipeId) continue;
        const current = recipeMap.get(recipeId) || {
          recipeId,
          recipeName: row?.recipeName || `Tarif #${recipeId}`,
          category: row?.category || null,
          counts: { view: 0, select: 0, plan: 0, cook: 0 }
        };
        const nextCounts = row?.counts || {};
        current.counts.view = Math.max(current.counts.view, Number(nextCounts.view || 0));
        current.counts.select = Math.max(current.counts.select, Number(nextCounts.select || 0));
        current.counts.plan = Math.max(current.counts.plan, Number(nextCounts.plan || 0));
        current.counts.cook = Math.max(current.counts.cook, Number(nextCounts.cook || 0));
        if (!current.category && row?.category) current.category = row.category;
        recipeMap.set(recipeId, current);
      }
    };

    pushRows(recipePerformance?.topCooked || []);
    pushRows(recipePerformance?.highViewLowSelect || []);
    pushRows(recipePerformance?.selectedNotCooked || []);
    pushRows(unusedRecipes?.lists?.neverViewed || []);
    pushRows(unusedRecipes?.lists?.viewedNotSelected || []);
    pushRows(unusedRecipes?.lists?.selectedNotCooked || []);

    const typeAgg = new Map();
    for (const row of recipeMap.values()) {
      const typeKey = normalizeKitchenType(row.category, row.recipeName);
      if (!typeAgg.has(typeKey)) {
        typeAgg.set(typeKey, { typeKey, label: KITCHEN_TYPE_LABELS[typeKey], view: 0, select: 0, plan: 0, cook: 0 });
      }
      const item = typeAgg.get(typeKey);
      item.view += row.counts.view;
      item.select += row.counts.select;
      item.plan += row.counts.plan;
      item.cook += row.counts.cook;
    }
    return withRates(Array.from(typeAgg.values()).sort((a, b) => b.view - a.view));
  }, [recipePerformance, unusedRecipes]);

  const typeRows = useMemo(() => {
    const apiRows = Array.isArray(kitchenTypeIntelligence?.distribution)
      ? kitchenTypeIntelligence.distribution.map((row) => ({
          ...row,
          label: row?.label || KITCHEN_TYPE_LABELS[row?.typeKey] || 'Diğer'
        }))
      : null;
    return apiRows && apiRows.length > 0 ? withRates(apiRows) : fallbackTypeRows;
  }, [kitchenTypeIntelligence?.distribution, fallbackTypeRows]);

  const segmentTypePairs = useMemo(() => {
    const apiPairs = Array.isArray(kitchenTypeIntelligence?.segmentTypePairs) ? kitchenTypeIntelligence.segmentTypePairs : null;
    if (apiPairs && apiPairs.length > 0) return apiPairs;

    const currentSegmentKey = String(filters.segment || '').trim();
    const currentSegmentLabel = SEGMENT_LABELS[currentSegmentKey] || 'Genel Kitle';
    return typeRows.slice(0, 3).map((row, index) => ({
      id: `${currentSegmentKey || 'general'}-${row.typeKey}-${index}`,
      segmentLabel: currentSegmentLabel,
      typeLabel: row.label,
      view: row.view,
      select: row.select,
      plan: row.plan,
      cook: row.cook,
      confidence: row.view >= 20 ? 'YÜKSEK' : row.view >= 8 ? 'ORTA' : 'DÜŞÜK',
      lowSample: row.view < 8
    }));
  }, [kitchenTypeIntelligence?.segmentTypePairs, filters.segment, typeRows]);

  const decisionLayer = useMemo(() => {
    const sampleSize = typeRows.reduce((sum, row) => sum + row.view, 0);
    const confidence = guvenBandiHesapla(sampleSize, { yuksek: 80, orta: 25 });
    const anomalies = [];

    if (Number(funnel?.conversion?.planToCook || 0) > 100) {
      anomalies.push({
        title: 'Plan -> Pişirme %100 üzerinde',
        meaning: 'Pişirme adımı plan adımından fazla sayılmış görünüyor.',
        risk: 'Huni sırası bozulmuş veya aynı olay birden fazla yazılmış olabilir.',
        recommendation: 'Huni olay sıralaması ve mükerrer kayıtlar kontrol edilmeli.'
      });
    }
    if (Number(decisionFeedback?.counts?.shown || 0) === 0 && Number(decisionFeedback?.rates?.acceptRate || 0) > 0) {
      anomalies.push({
        title: 'Gösterim 0 iken kabul oranı var',
        meaning: 'Kabul olayı görünürken gösterim olayı eksik.',
        risk: 'İzleme zinciri kopuksa karar metrikleri yanlış okunur.',
        recommendation: 'Gösterim ve kabul olay sözleşmesi aynı filtre penceresinde doğrulanmalı.'
      });
    }
    if (Number(funnel?.cook || 0) > Number(funnel?.view || 0)) {
      anomalies.push({
        title: 'Pişirme, görüntülemeden yüksek',
        meaning: 'Son adım, başlangıç adımından büyük görünüyor.',
        risk: 'Zaman penceresi veya olay tekilleştirme kuralı hatalı olabilir.',
        recommendation: 'Olay sıralaması, pencere hizası ve tekilleştirme kuralı kontrol edilmeli.'
      });
    }

    const bestCookType = [...typeRows].sort((a, b) => b.planToCook - a.planToCook)[0];
    const lowViewHighCook = bestCookType && bestCookType.planToCook >= 60 && bestCookType.view > 0;
    const apiInsight = Array.isArray(kitchenTypeIntelligence?.insights) && kitchenTypeIntelligence.insights[0]
      ? kitchenTypeIntelligence.insights[0]
      : null;

    const narrative = apiInsight?.message || (
      lowViewHighCook
        ? `${bestCookType.label} tipinde görüntüleme görece düşük ama plan->pişirme güçlü; görünürlüğü artırmak yüksek etki üretebilir.`
        : 'Huni genelinde görüntüleme->seçim kaybı yüksek; öneri eşleşmesi ve başlık kalitesi birlikte iyileştirilmeli.'
    );

    const keyTakeaway = lowViewHighCook
      ? `Kullanıcı ${bestCookType.label} tipinde içeriği tamamlıyor; sorun öneri kalitesinden çok görünürlükte.`
      : 'Kullanıcı öneriyi görüyor ama seçimde kayıp yaşıyor; sorun akış ve öneri sunum kalitesinde.';

    const actions = [];
    const segmentLabel = SEGMENT_LABELS[String(filters.segment || '')] || 'Genel Kitle';
    const pencereLabel = String(filters.windowLabel || filters.window || 'Son 30 Gün');
    const { actionLock: globalActionLock, actionLockReason: globalActionLockReason } = aksiyonKilidiHesapla({
      anomaliSayisi: anomalies.length,
      guvenBandi: confidence,
      anomaliMesaji: 'Bu aksiyon şu an uygulanamaz: veri güveni düşük / funnel akışı tutarsız.',
      dusukOrneklemMesaji: 'Bu aksiyon şu an uygulanamaz: örneklem düşük, önce gözlem modunda izlenmeli.'
    });

    if (apiInsight?.action) {
      const score = aksiyonSkoruHesapla(6, confidence, sampleSize, { yuksek: 80, orta: 25 });
      actions.push({
        actionKey: 'api-icgoru',
        title: apiInsight.action,
        reason: 'Otomatik içgörü bu eylemi öncelikli buldu.',
        doNotApplyWhen: 'Örneklem düşük veya funnel anomali durumunda doğrudan canlıya alınmamalı.',
        expectedImpact: 'Kabul ve plan->pişirme oranında kontrollü artış beklenir.',
        affectedSegment: segmentLabel,
        score,
        enabled: !globalActionLock,
        lockReason: globalActionLockReason,
        impacts: ['+%3 Kabul oranı', '+%2 Plan -> Pişirme'],
        baselineExpected: { metric: 'Kabul oranı', baseline: '%48', expected: '%51' },
        context: { orneklem: sampleSize, segment: segmentLabel, yemekTipi: bestCookType?.label || 'Genel', pencere: pencereLabel },
        status: 'uygulanmadı',
        resultDelta: '',
        updatedAt: new Date().toLocaleDateString('tr-TR'),
        whyThisAction: ['Kabul sinyali var ancak tamamlanma düşük.', 'Öneri sonrası adımda sürtünme görülüyor.']
      });
    }
    if (lowViewHighCook) {
      const score = aksiyonSkoruHesapla(8, confidence, sampleSize, { yuksek: 80, orta: 25 });
      actions.push({
        actionKey: `tip-gorunurluk-${bestCookType?.typeKey || 'genel'}`,
        title: `${bestCookType.label} tariflerini üst sıralarda daha sık göster`,
        reason: `${bestCookType.label} tipinde plan->pişirme güçlü, görünürlük düşük.`,
        doNotApplyWhen: 'Oturum sayısı çok düşükse veya olay sıralaması tutarsızsa uygulanmamalı.',
        expectedImpact: `${bestCookType.label} tipinde plan->pişirme +4 ila +8 puan`,
        affectedSegment: `${segmentLabel} içinde ${bestCookType.label} eğilimi olan kullanıcılar`,
        score,
        enabled: !globalActionLock,
        lockReason: globalActionLockReason,
        impacts: ['+%5 Pişirme oranı', '+%3 Dönüşüm oranı', '+120 kullanıcı etki alanı'],
        baselineExpected: { metric: 'Pişirme oranı', baseline: `%${Number(funnel?.conversion?.planToCook || 0).toLocaleString('tr-TR')}`, expected: `%${Number((Number(funnel?.conversion?.planToCook || 0) + 5).toFixed(2)).toLocaleString('tr-TR')}` },
        context: { orneklem: sampleSize, segment: segmentLabel, yemekTipi: bestCookType.label, pencere: pencereLabel },
        status: 'uygulanmadı',
        resultDelta: '',
        updatedAt: new Date().toLocaleDateString('tr-TR'),
        whyThisAction: [
          `${bestCookType.label} tipinde plan->pişirme yüksek.`,
          'Görüntüleme düşük olduğu için görünürlük artışı doğrudan etki üretebilir.'
        ]
      });
    }
    const flowStepScore = aksiyonSkoruHesapla(5, confidence, sampleSize, { yuksek: 80, orta: 25 });
    actions.push({
      actionKey: 'adim-sadelestir',
      title: 'Öneri sonrası adımı 1 adıma indir',
      reason: 'Kabul var fakat seçim/plan geçişi zayıf; sürtünme ara adımda.',
      doNotApplyWhen: 'Gösterim ve kabul olayları arasında tutarlılık sorunu varsa önce veri doğrulanmalı.',
      expectedImpact: 'Görüntüleme->seçim dönüşümünde +2 ila +5 puan',
      affectedSegment: segmentLabel,
      score: flowStepScore,
      enabled: !globalActionLock,
      lockReason: globalActionLockReason,
      impacts: ['+%5 Seçim oranı', '+%3 Plan oranı', '+90 kullanıcı etki alanı'],
      baselineExpected: {
        metric: 'Görüntüleme -> Seçim',
        baseline: `%${Number(funnel?.conversion?.viewToSelect || 0).toLocaleString('tr-TR')}`,
        expected: `%${Number((Number(funnel?.conversion?.viewToSelect || 0) + 5).toFixed(2)).toLocaleString('tr-TR')}`
      },
      context: { orneklem: sampleSize, segment: segmentLabel, yemekTipi: 'Genel', pencere: pencereLabel },
      status: 'uygulanmadı',
      resultDelta: '',
      updatedAt: new Date().toLocaleDateString('tr-TR'),
      whyThisAction: [
        'Kabul olmasına rağmen seçim ve plan geçişi zayıf.',
        'Ara adım azaltımı sürtünmeyi düşürüp dönüşümü artırabilir.'
      ]
    });
    if (Number(unusedRecipes?.totals?.selectedNotCookedCount || 0) > 0) {
      const score = aksiyonSkoruHesapla(4, confidence, sampleSize, { yuksek: 80, orta: 25 });
      actions.push({
        actionKey: 'hazirlik-alternatifi',
        title: 'Seçilip pişirilmeyen tariflere hızlı hazırlık alternatifi ekle',
        reason: 'Seçimden sonra tamamlanmayan içerik sayısı yüksek.',
        doNotApplyWhen: 'Örneklem 20 altındaysa önce gözlem modunda izlenmeli.',
        expectedImpact: 'Seçim->plan ve plan->pişirme hattında toparlanma',
        affectedSegment: 'Seçilip pişirmeyen kullanıcı kümeleri',
        score,
        enabled: !globalActionLock,
        lockReason: globalActionLockReason,
        impacts: ['+%3 Plan oranı', '+%2 Pişirme oranı'],
        baselineExpected: { metric: 'Seçim -> Plan', baseline: `%${Number(funnel?.conversion?.selectToPlan || 0).toLocaleString('tr-TR')}`, expected: `%${Number((Number(funnel?.conversion?.selectToPlan || 0) + 3).toFixed(2)).toLocaleString('tr-TR')}` },
        context: { orneklem: sampleSize, segment: segmentLabel, yemekTipi: 'Genel', pencere: pencereLabel },
        status: 'uygulanmadı',
        resultDelta: '',
        updatedAt: new Date().toLocaleDateString('tr-TR'),
        whyThisAction: ['Seçilip tamamlanmayan tarif sayısı yüksek.', 'Hazırlık zorluğu azaltımı tamamlama ihtimalini artırır.']
      });
    }

    const priorityScore = (anomalies.length * 25) + (lowViewHighCook ? 40 : 20) + (Number(unusedRecipes?.totals?.unusedCount || 0) > 0 ? 20 : 0);
    const priority = priorityScore >= 70 ? 'HIGH' : priorityScore >= 45 ? 'MEDIUM' : 'LOW';
    const expectedOutcome = lowViewHighCook
      ? `${bestCookType.label} görünürlüğü artarsa plan->pişirme +4 ila +8 puan beklenir.`
      : 'Görüntüleme->seçim iyileştirmesiyle kabul oranında +2 ila +5 puan hedeflenir.';

    const actionsWithLock = aksiyonlaraKilitUygula(actions, globalActionLock, globalActionLockReason);

    return {
      narrative,
      keyTakeaway,
      actions: actionsWithLock,
      anomalies,
      priority,
      confidence,
      sampleSize,
      expectedOutcome,
      actionLock: globalActionLock,
      actionLockReason: globalActionLockReason
    };
  }, [
    typeRows,
    funnel,
    decisionFeedback,
    unusedRecipes,
    kitchenTypeIntelligence?.insights,
    filters.segment,
    filters.window,
    filters.windowLabel
  ]);

  const unusedRescue = useMemo(() => {
    const unusedCount = Number(unusedRecipes?.totals?.unusedCount || 0);
    const selectedNotCooked = Number(unusedRecipes?.totals?.selectedNotCookedCount || 0);
    const potentialValueTry = Math.round((unusedCount * 48) + (selectedNotCooked * 92));
    const suggestions = [
      'Domates ağırlıklı tarifleri hızlı sos/menemen alternatifiyle yeniden konumlandır.',
      'Sebze yoğun tarifleri çorba ve tencere akışına taşıyarak pişirme olasılığını yükselt.',
      'Seçilip pişirilmeyen tariflerde hazırlık süresi düşük alternatif göster.'
    ];
    return { unusedCount, potentialValueTry, suggestions };
  }, [unusedRecipes]);

  const actionStateScope = olusturAksiyonDurumKapsami('funnel', entity);

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `funnel-detay-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <DecisionEngineBlock
        narrative={decisionLayer.narrative}
        keyTakeaway={decisionLayer.keyTakeaway}
        actions={decisionLayer.actions}
        priority={decisionLayer.priority}
        confidence={decisionLayer.confidence}
        sampleSize={decisionLayer.sampleSize}
        anomalies={decisionLayer.anomalies}
        expectedOutcome={decisionLayer.expectedOutcome}
        actionLock={decisionLayer.actionLock}
        actionLockReason={decisionLayer.actionLockReason}
        actionStateScope={actionStateScope}
      />

      {funnel ? (
        <>
          <section className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs text-slate-500 dark:text-slate-400">Huni Eğilimi (özet)</p>
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
              Görüntüleme {Number(funnel.view || 0).toLocaleString('tr-TR')} -> Seçim {Number(funnel.select || 0).toLocaleString('tr-TR')} -> Plan {Number(funnel.plan || 0).toLocaleString('tr-TR')} -> Pişirme {Number(funnel.cook || 0).toLocaleString('tr-TR')}
            </p>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Dönüşüm Kırılımı</p>
            <ul className="mt-2 space-y-1.5">
              <Metric label="Görüntüleme -> Seçim" value={`${Number(funnel?.conversion?.viewToSelect || 0).toLocaleString('tr-TR')}%`} />
              <Metric label="Seçim -> Plan" value={`${Number(funnel?.conversion?.selectToPlan || 0).toLocaleString('tr-TR')}%`} />
              <Metric label="Plan -> Pişirme" value={`${Number(funnel?.conversion?.planToCook || 0).toLocaleString('tr-TR')}%`} />
            </ul>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Düşüş Kırılımı (özet)</p>
            <ul className="mt-2 space-y-1.5">
              <Metric label="Görüntüleme -> Seçim" value={`${Number(funnel?.dropOff?.viewToSelect || 0).toLocaleString('tr-TR')}%`} />
              <Metric label="Seçim -> Plan" value={`${Number(funnel?.dropOff?.selectToPlan || 0).toLocaleString('tr-TR')}%`} />
              <Metric label="Plan -> Pişirme" value={`${Number(funnel?.dropOff?.planToCook || 0).toLocaleString('tr-TR')}%`} />
            </ul>
          </section>
        </>
      ) : null}

      {typeRows.length > 0 ? (
        <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Yemek Tipi Dağılımı</p>
          <ul className="mt-2 space-y-2">
            {typeRows.map((row) => (
              <li key={`dist-${row.typeKey}`} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-2 text-sm dark:border-slate-700 dark:bg-slate-800">
                <p className="font-bold text-slate-800 dark:text-slate-100">{row.label}</p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{categoryHint(row.typeKey)}</p>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
                  <span>Görüntüleme: {row.view.toLocaleString('tr-TR')}</span>
                  <span>Seçim: {row.select.toLocaleString('tr-TR')}</span>
                  <span>Plan: {row.plan.toLocaleString('tr-TR')}</span>
                  <span>Pişirme: {row.cook.toLocaleString('tr-TR')}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {typeRows.length > 0 ? (
        <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Yemek Tipi -> Huni</p>
          <ul className="mt-2 space-y-2">
            {typeRows.map((row) => (
              <li key={`funnel-${row.typeKey}`} className="text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-700 dark:text-slate-200">{row.label}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {row.view.toLocaleString('tr-TR')} -> {row.select.toLocaleString('tr-TR')} -> {row.plan.toLocaleString('tr-TR')} -> {row.cook.toLocaleString('tr-TR')}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-600 dark:text-slate-300">
                  <span>G->S: %{row.viewToSelect.toLocaleString('tr-TR')}</span>
                  <span>S->P: %{row.selectToPlan.toLocaleString('tr-TR')}</span>
                  <span>P->Pi: %{row.planToCook.toLocaleString('tr-TR')}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {segmentTypePairs.length > 0 ? (
        <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Segment + Yemek Tipi</p>
          <ul className="mt-2 space-y-2">
            {segmentTypePairs.map((row) => (
              <li key={row.id} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-2 text-sm dark:border-slate-700 dark:bg-slate-800">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-slate-800 dark:text-slate-100">{row.segmentLabel} + {row.typeLabel}</p>
                  <span className="text-[10px] font-bold text-slate-500 dark:text-slate-300">Güven: {row.confidence}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
                  <span>Görüntüleme: {Number(row.view || 0).toLocaleString('tr-TR')}</span>
                  <span>Seçim: {Number(row.select || 0).toLocaleString('tr-TR')}</span>
                  <span>Plan: {Number(row.plan || 0).toLocaleString('tr-TR')}</span>
                  <span>Pişirme: {Number(row.cook || 0).toLocaleString('tr-TR')}</span>
                </div>
                {row.lowSample ? <p className="mt-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400">Düşük Örneklem</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {decisionFeedback ? (
        <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Karar Geri Bildirimi</p>
          <ul className="mt-2 space-y-1.5">
            <Metric label="Kabul" value={`${Number(decisionFeedback?.rates?.acceptRate || 0).toLocaleString('tr-TR')}%`} />
            <Metric label="Geçiş" value={`${Number(decisionFeedback?.rates?.skipRate || 0).toLocaleString('tr-TR')}%`} />
            <Metric label="Wow" value={`${Number(decisionFeedback?.rates?.wowRate || 0).toLocaleString('tr-TR')}%`} />
            <Metric label="Gösterim" value={Number(decisionFeedback?.counts?.shown || 0).toLocaleString('tr-TR')} />
          </ul>
        </section>
      ) : null}

      {recipePerformance ? (
        <section className="space-y-3">
          <RecipeList title="En Çok Pişirilen İlk 10 Tarif" rows={recipePerformance?.topCooked} valueKey="cook" />
          <RecipeList title="Yüksek Görüntüleme, Düşük Seçim (İlk 10)" rows={recipePerformance?.highViewLowSelect} valueKey="view" />
          <RecipeList title="Seçilip Pişirilmeyen Tarifler (İlk 10)" rows={recipePerformance?.selectedNotCooked} valueKey="select" />
        </section>
      ) : null}

      {unusedRecipes ? (
        <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Kullanılmayan Tarif Dağılımı</p>
          <ul className="mt-2 space-y-1.5">
            <Metric label="Hiç Gösterilmeyen" value={Number(unusedRecipes?.totals?.neverViewedCount || 0).toLocaleString('tr-TR')} />
            <Metric label="Gösterilip Seçilmeyen" value={Number(unusedRecipes?.totals?.viewedNotSelectedCount || 0).toLocaleString('tr-TR')} />
            <Metric label="Seçilip Pişirilmeyen" value={Number(unusedRecipes?.totals?.selectedNotCookedCount || 0).toLocaleString('tr-TR')} />
          </ul>
        </section>
      ) : null}

      {unusedRecipes ? (
        <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Kurtarma Potansiyeli</p>
          <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
            {unusedRescue.unusedCount.toLocaleString('tr-TR')} kullanılmayan tarif -> potansiyel değer: {unusedRescue.potentialValueTry.toLocaleString('tr-TR')} TL
          </p>
          <ul className="mt-2 space-y-1.5 text-sm text-slate-700 dark:text-slate-200">
            {unusedRescue.suggestions.map((item, index) => (
              <li key={`rescue-${index}`} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 dark:border-slate-700 dark:bg-slate-800">
                • {item}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <DrawerFooterActions onClose={onClose} onExport={handleExport} />
    </div>
  );
}



